import { formatDate } from "@angular/common";
import { Component_MAWP } from "./component_mawp";
import { Component_MAEP } from "./component_maep";
import { CalcTemplate } from "./calctemplate";
import { LTA_module } from "./lta_module";
import { GML_module } from "./gml_module";

type UnitSys = "US" | "SI";

export interface ComponentReportData {
  facility: string;
  plant: string;
  equipment: string;
  analyst: string;
  unitIsUs: boolean;
  intEquip: number;
  isTank: boolean;
  designPressureOrMFH?: number;
  designTemperature?: number;
  tNom?: number;
  tRd?: number;
  fca?: number;
  tC: number;
  tMin: number;
  mawpOrMfh?: number;
  eLong?: number;
  eCirc?: number;
  rsfA: number;
  tam: number;
  tmm: number;
  fcaMl: number;
  isExternal: boolean;
}

export interface TypeAData {
  selection: 0 | 1 | 2 | 3 | 4 | 5;
  input1_label?: string;
  input1_value?: string;
  input2_label?: string;
  input2_value?: string;
  screeningPass: boolean;
}

class MAWPAdapter {
  static toReportData(c: typeof Component_MAWP): ComponentReportData {
    return {
      facility: c.strFacility,
      plant: c.strPlant,
      equipment: c.strEquipment,
      analyst: c.strAnalyst,
      unitIsUs: c.intUnit_select === 0,
      intEquip: c.intEquip,
      isTank: c.intEquip === 8,
      designPressureOrMFH: c.sngP_d,
      designTemperature: c.sngTemp_d,
      tNom: c.intEquip !== 8 ? c.sngtnom : undefined,
      tRd: c.intEquip !== 8 ? c.sngtrd : undefined,
      fca: c.intEquip !== 8 ? c.sngFCA : undefined,
      tC: c.sngtc,
      tMin: c.sngtmin,
      mawpOrMfh: c.intEquip !== 8 ? c.sngMAWP : CalcTemplate.sngMFH,
      eLong: c.sngE_L,
      eCirc: (c.intEquip <= 1 || c.intEquip >= 5) ? c.sngE_c : undefined,
      rsfA: CalcTemplate.sngRSF_a,
      tam: GML_module.sngtam,
      tmm: LTA_module.sngtmm,
      fcaMl: CalcTemplate.sngFCAml,
      isExternal: false
    };
  }
}

class MAEPAdapter {
  static toReportData(c: typeof Component_MAEP): ComponentReportData {
    return {
      facility: c.strFacility,
      plant: c.strPlant,
      equipment: c.strEquipment,
      analyst: c.strAnalyst,
      unitIsUs: c.intUnit_select === 0,
      intEquip: c.intEquip,
      isTank: c.intEquip === 8,
      designPressureOrMFH: c.sngP_d,
      designTemperature: undefined,
      tNom: c.intEquip !== 8 ? c.sngtnom : undefined,
      tRd: c.intEquip !== 8 ? c.sngtrd : undefined,
      fca: c.intEquip !== 8 ? c.sngFCA : undefined,
      tC: c.sngtc,
      tMin: c.sngtmin,
      mawpOrMfh: c.intEquip !== 8 ? c.sngP_d : CalcTemplate.sngMFH,
      eLong: undefined,
      eCirc: undefined,
      rsfA: CalcTemplate.sngRSF_a,
      tam: GML_module.sngtam,
      tmm: LTA_module.sngtmm,
      fcaMl: CalcTemplate.sngFCAml,
      isExternal: true
    };
  }
}

interface UnitFormatter {
  pressure(val: number): string;
  thickness3(val: number): string;
  thickness1(val: number): string;
  mfHeight(val: number): string;
  temp(val?: number): string;
  labels(): {
    designPressureOrMFH: string;
    temp: string;
    tNom: string;
    trd: string;
    fca: string;
    tC: string;
    tMin: string;
    mawpOrMfh: string;
  };
}

class UsFormatter implements UnitFormatter {
  pressure(v: number) { return `${v.toFixed(1)} psi`; }
  thickness3(v: number) { return `${v.toFixed(3)} inch`; }
  thickness1(v: number) { return `${v.toFixed(1)} inch`; }
  mfHeight(v: number) { return `${v.toFixed(2)} ft`; }
  temp(v?: number) { return v != null && !Number.isNaN(v) && v !== -1 ? `${v.toFixed(2)} deg-F` : "N/A"; }
  labels() {
    return {
      
      designPressureOrMFH: "Design pressure or vessel MAWP (psi)",
      temp: "Design temperature (deg-F)",
      tNom: "Nominal Thickness (inch)",
      trd: "Current measured (uniform) thickness, trd (inch)",
      fca: "Future corrosion allowance, FCA = FCAi + FCAe (inch)",
      tC: "Future corroded thickness, tc (inch)",
      tMin: "Minimum required thickness, tmin (inch)",
      mawpOrMfh: "MAWP of the component based on tc, MAWP (psi)"
    };
  }
}

class SiFormatter implements UnitFormatter {
  pressure(v: number) { return `${v.toFixed(2)} MPa`; }
  thickness3(v: number) { return `${v.toFixed(3)} mm`; }
  thickness1(v: number) { return `${v.toFixed(1)} mm`; }
  mfHeight(v: number) { return `${v.toFixed(2)} m`; }
  temp(v?: number) { return v != null && !Number.isNaN(v) && v !== -1 ? `${v.toFixed(2)} deg-C` : "N/A"; }
  labels() {
    return {
      designPressureOrMFH: "Design pressure or vessel MAWP (MPa)",
      temp: "Design temperature (deg-C)",
      tNom: "Nominal Thickness (mm)",
      trd: "Current measured (uniform) thickness, trd (mm)",
      fca: "Future corrosion allowance, FCA = FCAi + FCAe (mm)",
      tC: "Future corroded thickness, tc (mm)",
      tMin: "Minimum required thickness, tmin (mm)",
      mawpOrMfh: "MAWP of the component based on tc, MAWP (MPa)"
    };
  }
}

class GMLReportBuilder {
  constructor(private fmt: UnitFormatter) {}

  withHeader(d: ComponentReportData): string {
    const dt = formatDate(new Date(), "dd/MM/yyyy", "en-US");
    let s = "";
    s += "</br></br><center>API 579-1/ASME FFS-1 Standard 2021 Edition, Part 4</center>";
    s += "<center>Assessment of General Metal Loss</center></br></br>";
    s += `${dt}</br>`;
    s += "Page: 1</br></br></br>";
    s += `Facility: ${d.facility}</br>`;
    s += `Plant: ${d.plant}</br>`;
    s += `Equipment: ${d.equipment}</br>`;
    s += `Analyst: ${d.analyst}</br></br></br>`;
    s += this.applicabilityList();
    return s;
  }

  withBody(d: ComponentReportData, typeA: TypeAData, calcHtml: string): string {
    let s = "";

    s += "</br>Type A Component Screening Result:</br>";
    if (typeA.selection !== 0) {
      if (typeA.input1_label) s += `${typeA.input1_label} = ${typeA.input1_value ?? ""}</br>`;
      if (typeA.input2_label) s += `${typeA.input2_label} = ${typeA.input2_value ?? ""}`;
    }
    s += "</br>";
    s += typeA.screeningPass
      ? "The component meets the Type A screening criteria.</br></br>"
      : "The component deosn't meet the Type A screening criteria.</br></br>";

    s += "Component Information :</br></br>";
    s += `Component type = ${this.componentTypeText(d.intEquip)}</br>`;

    const L = this.fmt.labels();
    if (!d.isTank) {
      if (d.designPressureOrMFH != null) {
        const unitTxt = d.unitIsUs ? "psi" : "MPa";
        const designLabel = d.isExternal
          ? `Design external pressure or vessel MAEP (${unitTxt})`
          : `Design pressure or vessel MAWP (${unitTxt})`;
        s += `${designLabel} = ${this.fmt.pressure(d.designPressureOrMFH)}</br>`;
      }
    } else {
      s += `Design Maximum Fill Height (${this.fmt instanceof UsFormatter ? "ft" : "m"}) = ${this.fmt.mfHeight(CalcTemplate.sngMFH_d)}</br>`;
    }
    s += `${L.temp} = ${this.fmt.temp(d.designTemperature)}</br>`;

    if (!d.isTank && d.tNom != null) s += `${L.tNom} = ${this.fmt.thickness3(d.tNom)}</br>`;
    if (!d.isTank && d.tRd != null) s += `${L.trd} = ${this.fmt.thickness3(d.tRd)}</br>`;
    if (!d.isTank && d.fca != null) s += `${L.fca} = ${this.fmt.thickness3(d.fca)}</br>`;
    if (d.isTank) {
      s += `Current measured (uniform) thickness, trd (${this.fmt instanceof UsFormatter ? "inch" : "mm"}) = N/A</br>`;
      s += `Future corrosion allowance, FCA = FCAi + FCAe (${this.fmt instanceof UsFormatter ? "inch" : "mm"}) = N/A</br>`;
    }
    s += `${L.tC} = ${this.fmt.thickness3(d.tC)}</br>`;
    s += `${L.tMin} = ${this.fmt.thickness3(d.tMin)}</br>`;
    if (!d.isTank && d.mawpOrMfh != null) {
      const unitTxt = d.unitIsUs ? "psi" : "MPa";
      const label = d.isExternal
        ? `MAEP of the component based on tc, MAEP (${unitTxt})`
        : `MAWP of the component based on tc, MAWP (${unitTxt})`;
      s += `${label} = ${this.fmt.pressure(d.mawpOrMfh)}</br>`;
    } else if (d.isTank && d.mawpOrMfh != null) {
      s += `Maximum Fill Height based on tc, MFH (${this.fmt instanceof UsFormatter ? "ft" : "m"}) = ${this.fmt.mfHeight(d.mawpOrMfh)}</br></br>`;
    }

    s += '<div class="pageBreak"></div>';

    s += "</br></br><center>API 579-1/ASME FFS-1 Standard 2021 Edition, Part 4</center>";
    s += "<center>Assessment of General Metal Loss</center></br></br>";
    const dt = formatDate(new Date(), "dd/MM/yyyy", "en-US");
    s += `${dt}</br>`;
    s += "Page: 2</br></br></br>";
    if (d.eLong != null) s += `Longitudinal weld joint efficiency, E_L = ${d.eLong.toFixed(2)}</br>`;
    if (d.eCirc != null) s += `Circumferential weld joint efficiency, E_C = ${d.eCirc.toFixed(2)}</br>`;
    s += `Allowable remaining strength factor, RSFa = ${d.rsfA.toFixed(2)}</br></br></br>`;

    s += "Metal Loss Information :</br></br>";
    s += GML_module.intanalysis_type == 0
      ? "Type of analysis = Average measured thickness</br>"
      : (d.isExternal ? "Type of analysis = MAEP</br>" : "Type of analysis = MAWP and MFH</br>");
    if (d.unitIsUs) {
      s += `Average measured thickness, tam (inch) = ${d.tam.toFixed(3)}</br>`;
      s += `Minimum measured thickness, tmm (inch) = ${d.tmm.toFixed(3)}</br>`;
      s += `Future corrosion allowance inside the damage area, FCAml (inch) = ${d.fcaMl.toFixed(3)}</br></br></br>`;
    } else {
      s += `Average measured thickness, tam (mm) = ${d.tam.toFixed(1)}</br>`;
      s += `Minimum measured thickness, tmm (mm) = ${d.tmm.toFixed(1)}</br>`;
      s += `Future corrosion allowance inside the damage area, FCAml (mm) = ${d.fcaMl.toFixed(1)}</br></br></br>`;
    }

    s += "Assessment Results :</br></br>";
    s += "All the conditions in the Applicability and Limitations section are met.</br></br>";
    s += calcHtml ?? "";
    return s;
  }

  build(header: string, body: string): string {
    return header + body;
  }

  private applicabilityList(): string {
    return `
      <ol class="appAndLimitList">
        <li>The original design criteria were in accordance with a recognized code or standard.</li>
        <li>The component is not operating in the creep range. The design temperature is less than or equal to the temperature limit in Table 4.1.</li>
        <li>The region of metal loss has relatively smooth contours without notches, i.e. negligible local stress concentrations.</li>
        <li>The component is not in cyclic service (i.e., less than 150 cycles in past and future planned operation or satisfying the cyclic service screening criteria).</li>
        <li>Type A components subject to internal pressure or external pressure. Type A components have a design equation that specifically relates pressure and/or other loads to a required wall thickness. Examples are:
          <ol type="a" class="appAndLimitList">
            <li>Pressure vessel cylindrical and conical shell sections satisfying the dimensions criteria</li>
            <li>Spherical pressure vessels and storage spheres</li>
            <li>Spherical, elliptical and torispherical formed heads</li>
            <li>Straight sections of piping systems and elbows or pipe bends that do not have structural attachments satisfying the size and temperature criteria</li>
            <li>Cylindrical atmospheric storage tank shell courses</li>
          </ol>
        </li>
      </ol>
      </br>`;
  }

  private componentTypeText(e: number): string {
    switch (e) {
      case 0: return "Cylinder";
      case 1: return "Cone";
      case 2: return "Sphere";
      case 3: return "Elliptical Head";
      case 4: return "F&D Head";
      case 5: return "B31.3 Piping";
      case 6: return "B31.4 Piping";
      case 7: return "B31.8 Piping";
      default: return "Tank";
    }
  }
}

export class GMLReportService {
  static generate(
    componentKind?: "MAWP" | "MAEP",
    typeA?: TypeAData,
    calcHtml?: string
  ): string {
    const useExternal = GML_module.intLoadingType === 1;
    const cKind = componentKind ?? (useExternal ? "MAEP" : "MAWP");

    const data: ComponentReportData = cKind === "MAWP"
      ? MAWPAdapter.toReportData(Component_MAWP)
      : MAEPAdapter.toReportData(Component_MAEP);

    const formatter: UnitFormatter = data.unitIsUs ? new UsFormatter() : new SiFormatter();
    const builder = new GMLReportBuilder(formatter);

    const header = builder.withHeader(data);
    const resolvedTypeA = typeA ?? this.readTypeAFromLocalStorage(data.unitIsUs ? 0 : 1);
    const calc = calcHtml ?? (GML_module.gmlCalcResult || "");
    const body = builder.withBody(data, resolvedTypeA, calc);

    return builder.build(header, body);
  }

  // Back-compat: read Type A data from localStorage
  private static readTypeAFromLocalStorage(unitSelect: number): TypeAData {
    const selection = parseInt(localStorage.getItem("TypeAradioselection") || "0", 10) as 0|1|2|3|4|5;
    const screeningPass = parseInt(localStorage.getItem("TypeAPassorFail") || "0", 10) === 1;
    const isUS = unitSelect === 0;

    const pick = (key: string) => {
      try { return JSON.parse(localStorage.getItem(key) || "{}"); } catch { return {}; }
    };

    let input1_label: string | undefined;
    let input2_label: string | undefined;
    let input1_value: string | undefined;
    let input2_value: string | undefined;

    if (selection === 1) {
      const src = pick("verticalinputs");
      input1_value = src.inputvalue1; input2_value = src.inputvalue2;
      input1_label = isUS ? "Vertical Location of the Flaw, H (inch)" : "Vertical Location of the Flaw, H* (mm) *";
      input2_label = isUS ? "Outside Diameter of Cylindrical or Conical Shell Section, OD (inch)" : "Outside Diameter of Cylindrical or Conical Shell Section, OD (mm) *";
    } else if (selection === 2) {
      const src = pick("horizontalinputs");
      input1_value = src.inputvalue1; input2_value = src.inputvalue2;
      input1_label = isUS ? "Outside Diameter of Cylindrical or Conical Shell Section, OD (inch) *" : "Outside Diameter of Cylindrical or Conical Shell Section, OD (mm) *";
      input2_label = isUS ? "Distance between saddle supports Lss (inch) *" : "Distance between saddle supports Lss (mm) *";
    } else if (selection === 3) {
      const src = pick("carbonsteelinputs");
      input1_value = src.inputvalue1; input2_value = src.inputvalue2;
      input1_label = isUS ? "Maximum Operating Temperature, Top (℉)" : "Maximum Operating Temperature, Top (℃)";
      input2_label = isUS ? "Pipe Nominal size, NPS (inch)" : "Pipe Nominal size, NPS (mm)";
    } else if (selection === 4) {
      const src = pick("highalloyinputs");
      input1_value = src.inputvalue1; input2_value = src.inputvalue2;
      input1_label = isUS ? "Maximum Operating Temperature, Top (℉)" : "Maximum Operating Temperature, Top (℃)";
      input2_label = isUS ? "Pipe Nominal size, NPS (inch)" : "Pipe Nominal size, NPS (mm)";
    }

    return { selection, input1_label, input1_value, input2_label, input2_value, screeningPass };
  }
}